// useCouponUserStore.js
// Store Pinia para o fluxo de vínculos coupon_users (elegibilidade TARGETED).
// Espelha o padrão de useCouponStore.js e useSubscriptionStore.js:
//   – filtros client-side espelhados em backend (debounce + refetch)
//   – filteredCouponUsers como getter para filtragem visual instantânea
//   – setFilter dispara fetchCouponUsersPaged voltando à pág. 1

import { defineStore } from "pinia";
import * as couponUserService from "@/services/couponUserService";

function hasFilterValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function toBooleanFilter(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return "";
}

export const useCouponUserStore = defineStore("couponUser", {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  state: () => ({
    couponUsers: [],
    selectedCouponUserIds: [],
    selectedCouponUser: null,
    error: null,

    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    // Filtro separado por user (força o próprio ID para não-Sys).
    // Segue o mesmo padrão de useSubscriptionStore.userIdFilter.
    userIdFilter: null,

    shouldForcePageOne: false,

    // Filtros enviados ao backend (e espelhados no painel visual)
    filters: {
      couponId: "", // substring numérica
      userId: "", // igualdade exata (travado para não-Sys)
      couponCode: "", // substring textual (LIKE)
      userName: "", // substring textual (LIKE)
      isRevoked: "", // "" | true | false
      isRedeemed: "", // "" | true | false
    },
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // GETTERS
  // ─────────────────────────────────────────────────────────────────────────
  getters: {
    isAllSelected: (state) => (total) =>
      state.selectedCouponUserIds.length === total,

    /**
     * Filtragem client-side idêntica ao backend, usada como espelho visual
     * enquanto o debounce está em voo. Quando o backend responde, couponUsers
     * já chega filtrada e os dois conjuntos convergem.
     */
    filteredCouponUsers: (state) => {
      const f = state.filters;

      return state.couponUsers.filter((cu) => {
        // ── couponId (substring numérica) ─────────────────────────────
        if (f.couponId.trim() !== "") {
          if (!String(cu.couponId ?? "").includes(f.couponId.trim()))
            return false;
        }

        // ── userId (igualdade exata) ──────────────────────────────────
        if (f.userId.trim() !== "") {
          if (String(cu.userId ?? "") !== f.userId.trim()) return false;
        }

        // ── couponCode (substring textual) ────────────────────────────
        if (f.couponCode.trim() !== "") {
          const code = (cu.couponCode ?? "").toLowerCase();
          if (!code.includes(f.couponCode.trim().toLowerCase())) return false;
        }

        // ── userName (substring textual) ──────────────────────────────
        if (f.userName.trim() !== "") {
          const name = (cu.userName ?? "").toLowerCase();
          if (!name.includes(f.userName.trim().toLowerCase())) return false;
        }

        // ── isRevoked ─────────────────────────────────────────────────
        if (hasFilterValue(f.isRevoked)) {
          const wantRevoked = toBooleanFilter(f.isRevoked);
          if (wantRevoked !== "") {
            const isRevoked = cu.revokedAt != null;
            if (isRevoked !== wantRevoked) return false;
          }
        }

        // ── isRedeemed ────────────────────────────────────────────────
        if (hasFilterValue(f.isRedeemed)) {
          const wantRedeemed = toBooleanFilter(f.isRedeemed);
          if (wantRedeemed !== "") {
            const isRedeemed = cu.redeemedAt != null;
            if (isRedeemed !== wantRedeemed) return false;
          }
        }

        return true;
      });
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────
  actions: {
    // ── Seleção ──────────────────────────────────────────────────────────
    toggleSelection(id) {
      const index = this.selectedCouponUserIds.indexOf(id);
      if (index > -1) {
        this.selectedCouponUserIds.splice(index, 1);
      } else {
        this.selectedCouponUserIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedCouponUserIds = [...ids];
    },

    clearSelection() {
      this.selectedCouponUserIds = [];
    },

    // ── Fetch paginado ────────────────────────────────────────────────────
    /**
     * Busca vínculos coupon_users paginados no backend com os filtros atuais.
     *
     * @param {number} pageNumber
     * @param {number} pageSize
     * @param {number|null} [legacyUserIdFilter] - forçado por setUserIdFilter
     *        (mantém compatibilidade com fluxos externos — userId do painel
     *        tem prioridade quando preenchido).
     */
    async fetchCouponUsersPaged(pageNumber, pageSize, legacyUserIdFilter) {
      try {
        const filters = {
          couponId: this.filters.couponId,
          userId:
            this.filters.userId ||
            (legacyUserIdFilter != null ? String(legacyUserIdFilter) : ""),
          couponCode: this.filters.couponCode,
          userName: this.filters.userName,
          isRevoked: this.filters.isRevoked,
          isRedeemed: this.filters.isRedeemed,
        };

        const response = await couponUserService.fetchCouponUsersPaged(
          pageNumber,
          pageSize,
          filters,
        );

        // O backend retorna { data, totalCount, currentPage, pageSize, totalPages }.
        const payload = response.data ?? {};
        this.couponUsers = payload.data ?? [];
        this.totalCount = payload.totalCount ?? 0;
        this.totalPages = payload.totalPages ?? 1;
        this.currentPage = pageNumber;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = { message };
        console.error("❌ Erro ao buscar coupon_users paginados:", message);
        throw new Error(message);
      }
    },

    // ── setUserIdFilter (fluxo externo análogo ao useSubscriptionStore) ──
    setUserIdFilter(userId) {
      this.userIdFilter = userId;
      this.filters.userId = userId != null ? String(userId) : "";
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchCouponUsersPaged(1, this.pageSize, userId);
    },

    // ── setFilter ─────────────────────────────────────────────────────────
    /**
     * Atualiza um filtro e dispara refetch voltando à página 1.
     * @param {string} field - chave de this.filters
     * @param {string|boolean} value
     */
    setFilter(field, value) {
      if (!(field in this.filters)) return;

      // Booleanos preservam true/false literal
      if (field === "isRevoked" || field === "isRedeemed") {
        this.filters[field] = toBooleanFilter(value);
      } else {
        this.filters[field] = value;
      }

      // userId segue o fluxo compatível com setUserIdFilter
      if (field === "userId") {
        const trimmed = String(value ?? "").trim();
        const parsed = trimmed === "" ? null : Number(trimmed);
        const isValidInt =
          trimmed === "" || (Number.isInteger(parsed) && parsed > 0);
        if (isValidInt) {
          this.setUserIdFilter(parsed);
        }
        return;
      }

      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchCouponUsersPaged(1, this.pageSize, this.userIdFilter);
    },

    // ── clearFilters ──────────────────────────────────────────────────────
    clearFilters(shouldRefetch = true) {
      this.filters = {
        couponId: "",
        userId: "",
        couponCode: "",
        userName: "",
        isRevoked: "",
        isRedeemed: "",
      };

      this.userIdFilter = null;
      this.currentPage = 1;
      this.shouldForcePageOne = true;

      if (shouldRefetch) {
        this.fetchCouponUsersPaged(1, this.pageSize, null);
      }
    },

    // ── Busca por ID ──────────────────────────────────────────────────────
    async getCouponUserById(id) {
      try {
        const response = await couponUserService.getCouponUserById(id);
        this.selectedCouponUser = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar coupon_user por ID:", message);
        throw new Error(message);
      }
    },

    // ── Criação ───────────────────────────────────────────────────────────
    async createCouponUser(data) {
      try {
        const response = await couponUserService.createCouponUser(data);
        this.shouldForcePageOne = true;
        await this.fetchCouponUsersPaged(1, this.pageSize, this.userIdFilter);
        return response.data;
      } catch (error) {
        // ⚠️ Backend retorna 400 se o cupom não for TARGETED — a mensagem
        //    explica a regra. Repasso o objeto original para o modal usar
        //    error.response.data.message.
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao criar coupon_user:", message);
        throw error;
      }
    },

    // ── Atualização ───────────────────────────────────────────────────────
    async updateCouponUser(data) {
      try {
        await couponUserService.updateCouponUser(data);
        await this.fetchCouponUsersPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar coupon_user:", message);
        throw new Error(message);
      }
    },

    // ── Delete unitário ───────────────────────────────────────────────────
    async deleteCouponUserById(id) {
      try {
        await couponUserService.deleteCouponUser(id);
        await this.fetchCouponUsersPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        // Se a página ficou vazia, regride uma página
        if (this.couponUsers.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponUsersPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupon_user:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote (seleção da store) ────────────────────────────────
    async deleteSelectedCouponUsers() {
      if (!this.selectedCouponUserIds.length) return;

      try {
        await couponUserService.deleteCouponUsersBatch(
          this.selectedCouponUserIds,
        );
        this.clearSelection();
        await this.fetchCouponUsersPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.couponUsers.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponUsersPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupon_users em lote:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote por IDs externos ───────────────────────────────────
    async deleteCouponUsersByIds(ids) {
      if (!ids || !ids.length) return;
      try {
        await couponUserService.deleteCouponUsersBatch(ids);
        this.selectedCouponUserIds = this.selectedCouponUserIds.filter(
          (id) => !ids.includes(id),
        );
        await this.fetchCouponUsersPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.couponUsers.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponUsersPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error(
          "❌ Erro ao excluir coupon_users em lote (by ids):",
          message,
        );
        throw new Error(message);
      }
    },
  },
});
